import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { TenantRoleViewModel } from '../api';
import { roleDisplayLabel } from '../roleLabel';

/** Confirms `DELETE /tenants/{uuid}/roles/{guid}` (PTV2-1426). */
export function DeleteTenantRoleConfirmDialog({
  role,
  onCancel,
  onConfirm,
  pending,
  submitError,
}: {
  readonly role: TenantRoleViewModel | null;
  readonly onCancel: () => void;
  readonly onConfirm: (role: TenantRoleViewModel) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const open = role !== null;
  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('routes.platform.detail.roles.delete.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.platform.detail.roles.delete.message', {
              name: role === null ? '' : roleDisplayLabel(role.name),
            })}
          </DialogDescription>
        </DialogHeader>
        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.platform.detail.roles.delete.errors.submit', { code: submitErrorCode })}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('routes.platform.detail.roles.delete.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={pending}
            onClick={() => {
              if (role !== null) onConfirm(role);
            }}
          >
            {t('routes.platform.detail.roles.delete.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
